'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';



import { usePathname, useRouter } from 'next/navigation';



import { useKeepAlive } from '@/api/useKeepAlive';
import { useOffers } from '@/api/useOffers';
import { useSessionGuard } from '@/api/useSessionGuard';
import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import { usePortalBackground } from '@/hooks/usePortalBackground';
import { useLongLoadingRum } from '@/hooks/useLongLoadingRum';
import {
  isOtpUrlAuthActive,
  shouldSkipStaleOtpRoundTripReconcile,
} from '@/lib/otpAuth';
import {
  installPortalJourneyUnloadListener,
  reconcileStalePortalJourneysOnLoad,
} from '@/lib/portalJourneyUnload';
import { shouldShowGlobalOverlay } from './wrapper.utils';
import { Loader } from '@/app/components/Loader/Loader';
import { EDesktopNavigationLayout, EEventsType, ELocalStorageKeys, EPageSection, EPlatform, EPlatformType, EPublisherEvents, EQueryParams, ESessionStorageKeys, EStorageType } from '@/lib/enums';
import { enrichOptions } from '@/lib/eventDataUtils';
import { EPortalRumEvents, logPortalRumInfo } from '@/lib/coralogix';
import { PORTAL_RENDERED_FLAG, announceLandingRender } from '@/lib/landingBeacon';
import { readLandingJourneyId } from '@/lib/landingJourney';
import { describePortalLanding } from '@/lib/portalLanding';
import { initMixpanel, isMixpanelInitialized, trackMixpanelEvent } from '@/lib/mixpanelClient';
import { handlePageClick } from '@/lib/pageClickUtils';
import { cn, getPageNameFromPathname, getUtmParams } from '@/lib/utils';
import { initialDesigns } from '@/state/app.state.service';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';
import { usePopupState } from '@/state/hooks/popup.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';



import DevBar from './appcharge/DevBar';
import Footer from './appcharge/Footer';
import { LoginModal } from './appcharge/Login/Components/LoginModal';
import { Menu } from './appcharge/Menu';
import { Topbar } from './appcharge/Topbar';
import AddToHomePage from './components/AddToHomePage';
import { v4 as uuidv4 } from 'uuid';





export const DEFAULT_TTL = 3600000;

export const Wrapper: React.FC<{
  initialPlatform: EPlatformType;
  initialDeviceType: EPlatform;
  domain: string;
  isOtpCredentialLanding?: boolean;
  children: React.ReactNode;
}> = ({
  initialPlatform,
  initialDeviceType,
  domain,
  isOtpCredentialLanding = false,
  children,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const isSupportPage = pathname?.startsWith('/support');
  const isFailedPage = pathname === '/failed';
  const utmParams = getUtmParams();
  const lastTrackedPathname = useRef<string | null>(null);
  const { trackPublisherUIEvent } = useTrackPublisherEvents();

  const {
    displayLoader,
    setDisplayLoader,
    playerData,
    setDesign,
    design,
    currentPageName,
    initialBoot,
  } = useAppState();

  const menuDesign = useMemo(() => initialBoot?.theme?.menu, [initialBoot?.theme?.menu]);
  const isTopFixedHeader =
    menuDesign?.desktopNavigationLayout === EDesktopNavigationLayout.TOP_FIXED_HEADER;

  const { isOtpLoggingIn, logout } = useLoginState();

  useKeepAlive(logout);
  useSessionGuard(logout);

  const checkAutoCollectParam = () => {
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href);

      return currentUrl.searchParams.has(EQueryParams.AUTO_COLLECT);
    }

    return false;
  };

  const hasAutoCollectParam = useMemo(() => {
    return checkAutoCollectParam();
  }, [pathname]);

  useEffect(() => {
    const hasParam = checkAutoCollectParam();
    if (hasParam) {
      setDisplayLoader(true);
    }
  }, [pathname, setDisplayLoader]);
  const { setPopupOffers, popUpStartHourUTC, setPopUpStartHourUTC } = usePopupState();
  const { getOffers } = useOffers({});

  usePortalBackground();

  const hasValidPageName = currentPageName && currentPageName.trim() !== '';

  const pageName = hasValidPageName
    ? currentPageName
    : getPageNameFromPathname(pathname, initialBoot?.pages);

  const getPageEventData = () => ({
    sessionMetaData_pageUrl: pathname,
    sessionMetaData_pageName: pageName,
    device_platform: initialPlatform,
    utmSource: utmParams.utm_source,
    utmMedium: utmParams.utm_medium,
    utmCampaign: utmParams.utm_campaign,
    player_id: playerData?.playerId,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
    ...utmParams,
  });

  useEffect(() => {
    const initializeMixpanel = async () => {
      try {
        await initMixpanel();
      } catch (error) {
        console.error('Failed to initialize Mixpanel:', error);
      }
    };

    initializeMixpanel();
  }, []);

  // This mount is the commit that ends the blank page: it is reached only once
  // the translations have settled and, on a credential landing, once the login
  // has too. The boot was announced long before, from a tree that showed nothing.
  useEffect(() => {
    announceLandingRender(PORTAL_RENDERED_FLAG);
  }, []);

  useEffect(() => {
    installPortalJourneyUnloadListener();
    reconcileStalePortalJourneysOnLoad(() =>
      shouldSkipStaleOtpRoundTripReconcile(isOtpCredentialLanding)
    );
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      webStorageUtil.remove(ESessionStorageKeys.TRACKED_PAGE);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const generateNewSession = () => {
    const newSessionId = uuidv4();
    const newTtl = Date.now() + DEFAULT_TTL;
    const sessionValue = {
      id: newSessionId,
      ttl: newTtl,
    };
    webStorageUtil.set(
      ESessionStorageKeys.SESSION_DATA,
      sessionValue,
      EStorageType.SESSION_STORAGE
    );
    webStorageUtil.set(ELocalStorageKeys.SESSION_DATA, sessionValue, EStorageType.LOCAL_STORAGE);
  };

  useEffect(() => {
    const sessionData = webStorageUtil.getStorageWithFallback();

    if (!sessionData || Date.now() > sessionData.ttl) {
      generateNewSession();
      webStorageUtil.remove(ESessionStorageKeys.TRACKED_PAGE);
    }

    // Recorded once per document, before any login can succeed. A player who
    // arrives from the game and leaves before finishing OTP fires no login and
    // no page event for the publisher, so this is the only trace that the
    // landing happened at all — and the app session id is what lets a backend
    // or publisher event, which does know the player, be joined to it.
    logPortalRumInfo(
      EPortalRumEvents.PORTAL_LANDED,
      describePortalLanding({
        pathname: window.location.pathname,
        search: window.location.search,
        platform: initialPlatform,
        storedPlayerId: webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerId,
        appSessionId: webStorageUtil.getStorageWithFallback()?.id,
        journeyId: readLandingJourneyId(),
      })
    );
  }, []);

  useEffect(() => {
    const isPathnameChanged = pathname !== lastTrackedPathname.current;

    if (isPathnameChanged && webStorageUtil.get(ESessionStorageKeys.TRACKED_PAGE) !== pathname) {
      lastTrackedPathname.current = pathname;
      webStorageUtil.set(ESessionStorageKeys.TRACKED_PAGE, pathname, EStorageType.SESSION_STORAGE);

      if (isMixpanelInitialized()) {
        trackMixpanelEvent(EEventsType.PORTAL_PAGE_DISPLAYED, getPageEventData());
      }

      logPortalRumInfo(EEventsType.PORTAL_PAGE_DISPLAYED, {
        playerId: playerData?.playerId,
        pageName,
        pathname,
      });

      trackPublisherUIEvent.mutate({
        ...enrichOptions({
          platform: initialPlatform,
          eventName: EPublisherEvents.PORTAL_PAGE_DISPLAYED,
          page: [{ pageUrl: pathname, pageName }],
          playerId: playerData?.playerId,
        }),
      });
    }
  }, [pathname, initialPlatform]);

  const refetchPopupOffers = useCallback(async () => {
    try {
      const newPopUpStartHourUTC = getOffers.data?.publisherSettings?.popUpStartHourUTC;
      if (newPopUpStartHourUTC != null && popUpStartHourUTC !== newPopUpStartHourUTC) {
        setPopUpStartHourUTC(newPopUpStartHourUTC);
      }
      if (getOffers.data?.offers?.popups) {
        setPopupOffers(getOffers.data.offers.popups);

        return;
      }

      const offersData = await getOffers.refetch();
      if (offersData.data?.offers?.popups) {
        setPopupOffers(offersData.data.offers.popups);
      }
      const refetchedStartHour = offersData.data?.publisherSettings?.popUpStartHourUTC;
      if (refetchedStartHour != null && popUpStartHourUTC !== refetchedStartHour) {
        setPopUpStartHourUTC(refetchedStartHour);
      }
    } catch (error) {
      console.error('Failed to refetch popup offers:', error);
    }
  }, [
    getOffers.data,
    setPopupOffers,
    popUpStartHourUTC,
    setPopUpStartHourUTC,
  ]);

  useEffect(() => {
    const playerDetails = playerData || {
      playerId: '',
      playerName: '',
      playerCountry: '',
    };

    if (playerDetails.playerId) {
      const lastVisitingPage = webStorageUtil.get(ELocalStorageKeys.LAST_VISITING_PAGE);
      if (lastVisitingPage) {
        router.push(lastVisitingPage);
        webStorageUtil.remove(ELocalStorageKeys.LAST_VISITING_PAGE);
      }
      refetchPopupOffers();
    }

    const shouldCleanAutoCollect = webStorageUtil.get(ELocalStorageKeys.AUTO_COLLECT_FLAG);

    if (shouldCleanAutoCollect) {
      const currentUrl = new URL(window.location.href);
      const hasAutoCollectParamLocal = currentUrl.searchParams.has(EQueryParams.AUTO_COLLECT);
      const hasPopupIdParam = currentUrl.searchParams.has(EQueryParams.POPUP_ID);
      const isOnStoreRoute = currentUrl.pathname.startsWith('/store');

      if (!isOnStoreRoute && (hasAutoCollectParamLocal || hasPopupIdParam)) {
        if (hasAutoCollectParamLocal) {
          currentUrl.searchParams.delete(EQueryParams.AUTO_COLLECT);
        }
        if (hasPopupIdParam) {
          currentUrl.searchParams.delete(EQueryParams.POPUP_ID);
        }
        window.history.replaceState({}, '', currentUrl.toString());
      }

      if (!isOnStoreRoute) {
        webStorageUtil.remove(ELocalStorageKeys.AUTO_COLLECT_FLAG);
      }
    }

    const hasParam = checkAutoCollectParam();
    if (!hasParam) {
      setDisplayLoader(false);
    }
  }, [playerData?.playerId, refetchPopupOffers, hasAutoCollectParam, pathname]);

  const handleDesignChange = () => {
    setDesign(design === initialDesigns[0] ? initialDesigns[1] : initialDesigns[0]);
  };

  const handlePageClickCallback = useCallback(
    (e: React.MouseEvent) => {
      handlePageClick({
        target: e.target,
        pathname,
        currentPageName,
        platform: initialPlatform,
        playerId: playerData?.playerId,
        trackPublisherUIEvent,
      });
    },
    [pathname, currentPageName, initialPlatform, playerData?.playerId]
  );

  const isGlobalOverlayVisible = shouldShowGlobalOverlay({
    displayLoader,
    hasAutoCollectParam,
    isOtpLoggingIn,
    isUrlOtpAuth: isOtpUrlAuthActive(),
  });
  let loadingReason = 'display-loader';
  if (isOtpLoggingIn) {
    loadingReason = 'otp-login';
  } else if (hasAutoCollectParam) {
    loadingReason = 'auto-collect';
  }

  useLongLoadingRum({
    context: {
      hasAutoCollectParam,
      isOtpLoggingIn,
      loadingReason,
      playerId: playerData?.playerId,
    },
    isLoading: isGlobalOverlayVisible,
    loadingArea: 'global-overlay',
  });

  return (
    <>
      {isGlobalOverlayVisible && <Loader />}
      {!isSupportPage && !isFailedPage ? (
        <>
          <DevBar />
          <LoginModal />
          <main
            className={cn(
              "flex min-h-screen flex-col gap-6 bg-background p-0 md:h-screen md:px-4 md:py-[22px]"
            )}
            onDoubleClick={handleDesignChange}
            onClick={handlePageClickCallback}>
            {/* Topbar */}
            <div data-page-section={EPageSection.HEADER}>
              <Topbar />
            </div>

            {/* Main Layout */}
            <div className="flex size-full flex-1 gap-6 overflow-hidden">
              {/* Menu */}
              {!isTopFixedHeader && (
                <aside className="hidden w-[30%] max-w-[336px] md:block" data-page-section={EPageSection.MENU}>
                  <Menu />
                </aside>
              )}

              {initialBoot && (
                <AddToHomePage
                  className="fixed bottom-4 left-4 z-50 ml-4 flex items-center justify-center"
                  portalBoot={initialBoot}
                  data-page-section={EPageSection.ADD_TO_HOME_PAGE}
                />
              )}

              {/* Main Content */}
              <div
                className='w-full flex-1 overflow-y-auto'
                data-page-section={EPageSection.CONTENT}>
                {isTopFixedHeader ? (
                  <div className="mx-auto max-w-[1025px]">
                    {children}
                  </div>
                ) : (
                  children
                )}
              </div>
            </div>
            <Footer />
          </main>
        </>
      ) : (
        <>
          {children}
          {!isFailedPage && (
            <div className="fixed bottom-0 left-1/2 w-full -translate-x-1/2 items-center justify-center md:max-w-[542px]">
              <Footer />
            </div>
          )}
        </>
      )}
    </>
  );
};
